import React from "react";
import { motion } from "framer-motion";
import {
  Home,
  Utensils,
  Baby,
  HeartHandshake,
  Stethoscope,
  ArrowRight,
} from "lucide-react";

const options = [
  {
    title: "Choose a Maid",
    desc: "Best for daily cleaning, home upkeep, and regular household support.",
    icon: Home,
    tone: "indigo",
  },
  {
    title: "Choose a Cook",
    desc: "Ideal for regular meals, kitchen prep, and home-style cooking.",
    icon: Utensils,
    tone: "orange",
  },
  {
    title: "Choose a Nanny",
    desc: "For attentive child care and daily routine support.",
    icon: Baby,
    tone: "pink",
  },
  {
    title: "Choose a Caretaker",
    desc: "For elderly assistance, companionship, and daily care needs.",
    icon: HeartHandshake,
    tone: "emerald",
  },
  {
    title: "Choose a Nurse",
    desc: "For patient care and home-based medical support.",
    icon: Stethoscope,
    tone: "sky",
  },
];

const toneStyles = {
  indigo: "from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
  orange: "from-orange-50 to-amber-50 border-orange-100 text-orange-700",
  pink: "from-pink-50 to-rose-50 border-pink-100 text-pink-700",
  emerald: "from-emerald-50 to-teal-50 border-emerald-100 text-emerald-700",
  sky: "from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
};

const NeedHelpChoosing = () => {
  return (
    <section className="relative px-4 py-16 md:py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f8f5ff_0%,#f2edff_45%,#fdf8ff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_84%_20%,rgba(168,85,247,0.08),transparent_20%)]" />

      <div className="relative max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Simple Decision Guide
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Not Sure What You Need?
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Choose the option that best matches your situation. We’ll help you move forward from there.
          </p>
        </motion.div>

        {/* Options */}
        <div className="space-y-4">
          {options.map((item, i) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                viewport={{ once: true }}
                whileHover={{ y: -3 }}
                className="group relative rounded-[26px] border border-white/70 bg-white/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] transition-all duration-300 p-5 md:p-6"
              >
                {/* Layout */}
                <div className="flex items-center justify-between gap-4">
                  {/* Left */}
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center border bg-gradient-to-br ${toneStyles[item.tone]}`}
                    >
                      <Icon className="w-5 h-5 md:w-6 md:h-6" />
                    </div>

                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
                        {item.title}
                      </h3>

                      <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="shrink-0">
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition" />
                  </div>
                </div>

                {/* Bottom divider */}
                <div className="mt-5 h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default NeedHelpChoosing;